import { useState } from "react"
import useCurrentLocationContext from "@contexts/currentLocationContext"
import { colors, fontSize, size } from "@styles/theme"
import { useAuthContext } from "@contexts/authContext"
import styled from "@emotion/styled/macro"
import useLoading from "@hooks/useLoading"
import axios from "@utils/webRequests"
import { Form, Slider, Toast } from "@base-ui/react"
import StyledAlert from "@elements/StyledAlert"
import { PiSpinnerBold } from "react-icons/pi"
import { TbMapPins } from "react-icons/tb"

const SliderControl = styled(Slider.Control)`
  display: flex;
  align-items: center;
  width: 100%;
  padding-block: 0.75rem;
  touch-action: none;
  user-select: none;
`

const SliderTrack = styled(Slider.Track)`
  width: 100%;
  height: 0.25rem;
  background-color: #333333;
  box-shadow: inset 0 0 0 1px #333333;
  border-radius: 0.25rem;
  user-select: none;
`

const SliderIndicator = styled(Slider.Indicator)`
  border-radius: 0.25rem;
  background-color: rgba(255,255,255,0.5);
  user-select: none;
`

const SliderThumb = styled(Slider.Thumb)`
  width: 1rem;
  height: 1rem;
  border-radius: 100%;
  background-color: ${colors.statusColors.right_now('primary')};
  user-select: none;

  &:has(:focus-visible) {
    outline: 2px solid blue;
  }
`

const SpinnerContainer = styled.div`
  position: absolute;
  top: ${size(1)};
  right: ${size(2)};

  display: flex;
  align-items: center;
  
  font-size: ${fontSize('sm')};
  color: grey;
`

const SpinnerValue = styled.div`
  padding-bottom: ${size(1)};
  width: 100%;
  display: flex;
  align-items: center;
`

const LocationRandomizerForm = ({  }) => {
  const { state: authState, handleInstantUpdate } = useAuthContext()
  const [formState, setFormState] = useState({ distance: authState.user?.lonlatOffset?.distance || 100 })
  const { trueLocation, setCurrentLocation } = useCurrentLocationContext()
  const { isLoading, isLoadingDelayed, setIsLoading } = useLoading()
  const toastManager = Toast.useToastManager()
  const randomizationIsDisabled = authState?.user?.travelModeEnabledAt || isLoading
  const randomizationIsGreyed = authState?.user?.travelModeEnabledAt || isLoadingDelayed

  const handleSliderChange = (distance) => {
    if (formState.distance !== distance) {
      setFormState({ ...formState, distance })
    }
  }

  const handleSliderCommit = (distance) => {
    if (formState.distance !== distance) {
      setFormState({ ...formState, distance })
    }
    handleSubmit(distance)
  }

  const handleSubmit = (distance) => {
    // Don't call again if loading
    if (isLoading) return;
    // Don't call again if the offset ditance is the same as what the user currently has
    if (formState.distance === authState?.user?.lonlatOffset?.distance) return;
    // Don't call if travel mode is enabled
    if (authState?.user?.travelModeEnabledAt) return;

    setIsLoading(true)
    axios.put(`api/my/profiles/locations`, { lonlat: trueLocation, distance })
      .then((res) => {
        // sets the new location in currentLocation context
        setCurrentLocation(res.data.lonlat)
        // updates the user in the auth state
        handleInstantUpdate(res.data)
        toastManager.add({
          text: 'location updated',
        });
      })
      .finally(() => setIsLoading(false))
      .catch((err) => {
        console.error(err)
        toastManager.add({
          text: 'location failed to update',
        });
      })
  }

  return (
    <Form style={{ width: '100%' }}>
      <Slider.Root
        style={{ position: 'relative' }}
        disabled={randomizationIsDisabled}
        name='distance'
        value={formState.distance}
        onValueChange={handleSliderChange}
        onValueCommitted={handleSliderCommit}
        defaultValue={formState.distance}
        step={100}
        min={100}
        max={2000}>
        {isLoadingDelayed &&
          <SpinnerContainer>
            <PiSpinnerBold
              size={size(2)}
              className='animation_rotation'
            />
            &nbsp;loading
          </SpinnerContainer>
        }
        <Slider.Value>
          {(props) => (
            <SpinnerValue style={{ color: randomizationIsGreyed && 'grey' }}>
              <TbMapPins />&nbsp;{props[0]} meters (away)
            </SpinnerValue>
          )}
        </Slider.Value>
        <SliderControl>
          <SliderTrack>
            <SliderIndicator />
            <SliderThumb style={{ backgroundColor: randomizationIsGreyed && 'grey' }}/>
          </SliderTrack>
        </SliderControl>
        {authState?.user?.travelModeEnabledAt &&
          <StyledAlert kind='info' AlertStyles={{ fontSize: fontSize('xs'), marginTop: size(2) }}>
            Turn off travel mode to randomize location
          </StyledAlert>
        }
      </Slider.Root>
    </Form>
  )
}

export default LocationRandomizerForm
